import { useEffect, useRef, useState } from 'react';

import DefaultFemaleAvatar from '../assets/Avatars/female_avatar.png';
import DefaultMaleAvatar from '../assets/Avatars/male_avatar.png';
import DefaultNonBAvatar from '../assets/Avatars/nonb_avatar.png';

import { useFile } from '../hooks/file.hooks';
import { Professional } from '@aldebaran/prisma';

const defaultAvatarByGender = {
  FEMALE: DefaultFemaleAvatar,
  MALE: DefaultMaleAvatar,
} as const;

export interface AvatarImageProps {
  avatarId?: string | null;
  gender?: Professional['gender'] | null;
  /** Accessible name; falls back to a generic label when empty. */
  alt: string;
}

/**
 * Renders a professional/practice avatar without the default-placeholder flash
 * (ALDO-2725).
 *
 * While a real avatar loads we hold a neutral background (never the gender
 * default), then fade the photo in once it has decoded. The gender default is
 * shown only when there is genuinely no avatar, the `/file/:id` lookup fails
 * with no usable URL, or the image itself fails to load — so professionals
 * without a usable photo are unaffected.
 *
 * Two pieces of state are keyed differently on purpose, because `GET /file/:id`
 * returns a fresh presigned URL on every call:
 * - the *fade* is keyed on the stable `avatarId`, so a refreshed URL for the
 *   same photo doesn't re-run the fade (or drop the avatar on a failed
 *   background refetch that keeps the cached URL);
 * - the *image-load failure* is keyed on the attempted URL, so a one-off failed
 *   load doesn't pin the avatar to the default forever — a later refetch that
 *   hands over a fresh URL is retried.
 */
export const AvatarImage = ({ avatarId, gender, alt }: AvatarImageProps) => {
  const { data, isError } = useFile(avatarId || undefined);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loadedAvatarId, setLoadedAvatarId] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const defaultSrc =
    defaultAvatarByGender[gender as keyof typeof defaultAvatarByGender] ??
    DefaultNonBAvatar;

  const url = data?.url;
  // The lookup has failed once the request has SETTLED (success or error) with no
  // usable URL — this includes a 200 that resolves `url: null`, which the API now
  // returns when the CDN base isn't configured (ALDO-2762). While still in-flight
  // (data undefined, not an error) this stays false, so we hold the neutral
  // background rather than flashing the gender default. react-query keeps the last
  // successful `data` through a failed background refetch, so a cached URL keeps
  // the avatar visible.
  const settled = data !== undefined || isError;
  const lookupFailed = settled && !url;
  // A failed <img> load is scoped to the URL that failed, so a fresh presigned
  // URL from a later refetch is retried instead of being stuck on the default.
  const imageFailed = !!url && failedUrl === url;
  const hasRealAvatar = Boolean(avatarId) && !lookupFailed && !imageFailed;
  const src = hasRealAvatar ? url : defaultSrc;

  // The bundled default art is available synchronously, so it never fades in.
  const revealed = hasRealAvatar ? loadedAvatarId === avatarId : true;

  // A cached image can finish before React attaches onLoad, so re-check on
  // mount/update. `complete` is true for broken images too, so use
  // `naturalWidth` to distinguish a successful decode from a failed load.
  useEffect(() => {
    if (!hasRealAvatar || !avatarId || !url) return;
    const img = imgRef.current;
    if (!img || !img.complete) return;
    if (img.naturalWidth > 0) setLoadedAvatarId(avatarId);
    else setFailedUrl(url);
  }, [hasRealAvatar, avatarId, url]);

  return (
    <div className="relative size-full overflow-hidden bg-[#F5F6F8]">
      {src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt || 'Avatar'}
          decoding="async"
          onLoad={() => {
            if (hasRealAvatar && avatarId) setLoadedAvatarId(avatarId);
          }}
          onError={() => {
            if (hasRealAvatar && url) setFailedUrl(url);
          }}
          className={`size-full object-cover transition-opacity duration-300 ${
            revealed ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
};
